import { API_BASE } from '../utils/apiBase';
// ============================================================
// STRIPE SERVICE - Payments, Identity Verification, and Connect Payouts
//  SECURITY FIX: All API calls now include Firebase Auth tokens

// ============================================================

import { loadStripe, Stripe } from '@stripe/stripe-js';
import { getAuth } from 'firebase/auth';
import { VerificationSession, ConnectOnboardingResult, PayoutResult } from '../types/complianceTypes';

// Initialize Stripe with publishable key from env
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

// Firebase Functions URLs - 2nd Gen Cloud Run
const FUNCTIONS_URL = API_BASE; // emulator-aware (see utils/apiBase)

// Standalone function URLs (2nd Gen Cloud Run)
const STANDALONE_URLS = {
  // Served through the API host (the standalone copies are private now)
  createIdentityVerification: `${API_BASE}/createIdentityVerification`,
  getVerificationStatus: `${API_BASE}/getVerificationStatus`,
  createConnectAccount: `${API_BASE}/createConnectAccount`,
  getConnectAccountStatus: `${API_BASE}/getConnectAccountStatus`,
  requestPayout: `${API_BASE}/requestPayout`,
};

// ============================================================
//  SECURITY: Authenticated Fetch Helper

// ============================================================

/**
 *  SECURITY FIX: All API calls now include Firebase Auth token

 * This ensures the server can verify the calling user's identity
 */
async function authedFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('User not authenticated');
  }

  const token = await user.getIdToken();

  const headers = new Headers(options.headers);
  headers.set('Content-Type', 'application/json');
  headers.set('Authorization', `Bearer ${token}`);

  return fetch(url, {
    ...options,
    headers,
  });
}

/**
 * Authenticated GET request
 */
async function authedGet(url: string): Promise<Response> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('User not authenticated');
  }

  const token = await user.getIdToken();

  return fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });
}

class StripeService {
  private stripe: Stripe | null = null;

  async getStripe(): Promise<Stripe | null> {
    if (!this.stripe) {
      this.stripe = await stripePromise;
    }
    return this.stripe;
  }

  // ============================================================
  // PLAYCOIN PURCHASES
  // ============================================================

  /**
   * Hosted checkout, shared by PlayCoins and PSS Coins purchases.
   *
   * The payment window is opened on the tap itself, BEFORE the server
   * round-trip, so popup blockers allow it. It is a NAMED window, so a
   * second tap reuses it instead of opening another. The app never leaves:
   * crypto goes to the Wallet screen that watches the order, cards keep
   * their same-tab flow. Only if no window could be opened at all does the
   * hosted page load in this tab as a last resort.
   */
  private async hostedCheckout(
    endpoint: string,
    body: Record<string, unknown>,
    usdAmount: number,
    label: string,
  ): Promise<{ success: boolean; error?: string }> {
    let win: Window | null = null;
    try {
      win = window.open('', 'pss-checkout', 'popup=yes,width=560,height=900');
      if (win) {
        win.document.open();
        win.document.write(
          '<!doctype html><html><head><title>PokerSweepStars payment</title></head>'
          + '<body style="margin:0;background:#0a1420;color:#e2e8f0;font-family:system-ui,sans-serif;display:flex;align-items:center;justify-content:center;height:100vh;text-align:center">'
          + '<div><div style="font-size:18px;font-weight:700;margin-bottom:8px">Opening the secure payment page</div>'
          + '<div style="color:#94a3b8;font-size:14px">One moment. Close this window at any time to go back to the app.</div></div></body></html>',
        );
        win.document.close();
      }
    } catch { win = null; }

    try {
      console.log(`💰 ${label}: $${usdAmount}`);
      const response = await authedFetch(`${FUNCTIONS_URL}/${endpoint}`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) {
        try { win?.close(); } catch { /* ignore */ }
        throw new Error(data.error || 'Failed to create checkout session');
      }

      if (data.url) {
        if (data.provider === 'nowpayments') {
          try {
            sessionStorage.setItem('pss:cryptoInvoice', JSON.stringify({ orderId: data.orderId, url: data.url, usd: usdAmount, ts: Date.now() }));
          } catch { /* ignore */ }
          if (win && !win.closed) {
            try { win.location.replace(data.url); } catch { win = null; }
          }
          if (win && !win.closed) {
            // The app stays right here, on the screen that watches the order
            window.location.assign(`/wallet?purchase=pending&order=${encodeURIComponent(String(data.orderId || ''))}`);
            return { success: true };
          }
          window.location.href = data.url; // last resort: no window could be opened
          return { success: true };
        }
        // Card checkout: same-tab flow, the placeholder window is not needed
        try { win?.close(); } catch { /* ignore */ }
        window.location.href = data.url;
        return { success: true };
      }

      const stripe = await this.getStripe();
      if (stripe && data.sessionId) {
        try { win?.close(); } catch { /* ignore */ }
        const { error } = await stripe.redirectToCheckout({ sessionId: data.sessionId });
        if (error) throw error;
        return { success: true };
      }

      try { win?.close(); } catch { /* ignore */ }
      throw new Error('No checkout URL or session ID returned');
    } catch (error) {
      try { win?.close(); } catch { /* ignore */ }
      console.error(`❌ ${label} error:`, error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Purchase PlayCoins
   *  SECURITY: Now sends auth token to server
   */
  async purchasePlayCoins(userId: string, usdAmount: number): Promise<{ success: boolean; error?: string }> {
    return this.hostedCheckout('createPlayCoinsCheckout', {
      amount: usdAmount,
      successUrl: `${window.location.origin}/wallet?purchase=success&type=playcoins`,
      cancelUrl: `${window.location.origin}/buy-coins?canceled=true`,
    }, usdAmount, `PlayCoins purchase for ${userId.substring(0, 8)}`);
  }

  /**
   * Deposit PKCash (real money)
   *  SECURITY: Now sends auth token to server
   */
  async depositPKCash(userId: string, usdAmount: number): Promise<{ success: boolean; error?: string }> {
    return this.hostedCheckout('createPKCashCheckout', {
      amount: usdAmount,
      successUrl: `${window.location.origin}/wallet?purchase=success&type=pkcash`,
      cancelUrl: `${window.location.origin}/deposit?canceled=true`,
    }, usdAmount, `PSS Coins purchase for ${userId.substring(0, 8)}`);
  }

  // ============================================================
  // STRIPE IDENTITY - KYC VERIFICATION
  // ============================================================

  /**
   * Create a Stripe Identity verification session
   *  SECURITY: Now sends auth token to server

   */
  async createVerificationSession(userId: string): Promise<VerificationSession | { error: string }> {
    try {
      console.log(`🔐 Creating Identity verification session for user ${userId}`);

      //  SECURITY: Use authedFetch instead of plain fetch

      const response = await authedFetch(STANDALONE_URLS.createIdentityVerification, {
        method: 'POST',
        body: JSON.stringify({
          // userId is now taken from auth token on server
          returnUrl: `${window.location.origin}/wallet?verification=complete`,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create verification session');
      }

      return {
        sessionId: data.sessionId,
        clientSecret: data.clientSecret,
        url: data.url,
        status: 'requires_input',
        expiresAt: new Date(data.expiresAt),
      };
    } catch (error) {
      console.error('❌ Identity verification error:', error);
      return {
        error: error instanceof Error ? error.message : 'Failed to start verification',
      };
    }
  }

  /**
   * Open Stripe Identity verification modal
   */
  async openVerificationModal(clientSecret: string): Promise<{ success: boolean; error?: string }> {
    try {
      const stripe = await this.getStripe();
      
      if (!stripe) {
        throw new Error('Stripe not initialized');
      }

      const { error } = await stripe.verifyIdentity(clientSecret);

      if (error) {
        console.error('❌ Verification modal error:', error);
        return {
          success: false,
          error: error.message,
        };
      }

      return { success: true };
    } catch (error) {
      console.error('❌ Error opening verification modal:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Verification failed',
      };
    }
  }

  /**
   * Redirect to Stripe hosted verification page
   */
  redirectToVerification(url: string): void {
    window.location.href = url;
  }

  /**
   * Get verification session status
   *  SECURITY: Now sends auth token to server

   */
  async getVerificationStatus(userId: string): Promise<{ status: string; verified: boolean }> {
    try {
      //  SECURITY: Use authedGet instead of plain fetch

      const response = await authedGet(`${STANDALONE_URLS.getVerificationStatus}?userId=${userId}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to get verification status');
      }

      return {
        status: data.status,
        verified: data.status === 'verified',
      };
    } catch (error) {
      console.error('❌ Error getting verification status:', error);
      return {
        status: 'unknown',
        verified: false,
      };
    }
  }

  // ============================================================
  // STRIPE CONNECT - PAYOUTS
  // ============================================================

  /**
   * Create a Stripe Connect Express account for the user
   *  SECURITY: Now sends auth token to server

   */
  async createConnectAccount(userId: string, email: string): Promise<ConnectOnboardingResult> {
    try {
      console.log(`🦅 Creating Connect account for user ${userId}`);

      //  SECURITY: Use authedFetch instead of plain fetch

      const response = await authedFetch(STANDALONE_URLS.createConnectAccount, {
        method: 'POST',
        body: JSON.stringify({
          // userId is now taken from auth token on server
          email,
          refreshUrl: `${window.location.origin}/wallet?connect=refresh`,
          returnUrl: `${window.location.origin}/wallet?connect=complete`,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create Connect account');
      }

      return {
        success: true,
        url: data.url,
      };
    } catch (error) {
      console.error('❌ Connect account creation error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create payout account',
      };
    }
  }

  /**
   * Get Connect account onboarding link (for returning users)
   *  SECURITY: Now sends auth token to server

   */
  async getConnectOnboardingLink(userId: string): Promise<ConnectOnboardingResult> {
    try {
      //  SECURITY: Use authedFetch instead of plain fetch

      const response = await authedFetch(`${FUNCTIONS_URL}/getConnectOnboardingLink`, {
        method: 'POST',
        body: JSON.stringify({
          // userId is now taken from auth token on server
          refreshUrl: `${window.location.origin}/wallet?connect=refresh`,
          returnUrl: `${window.location.origin}/wallet?connect=complete`,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to get onboarding link');
      }

      return {
        success: true,
        url: data.url,
      };
    } catch (error) {
      console.error('❌ Error getting onboarding link:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get onboarding link',
      };
    }
  }

  /**
   * Get user's Connect account status
   *  SECURITY: Now sends auth token to server

   */
  async getConnectAccountStatus(userId: string): Promise<{
    hasAccount: boolean;
    accountId?: string;
    payoutsEnabled: boolean;
    detailsSubmitted: boolean;
    requirements?: string[];
  }> {
    try {
      //  SECURITY: Use authedGet instead of plain fetch

      const response = await authedGet(`${STANDALONE_URLS.getConnectAccountStatus}?userId=${userId}`);
      const data = await response.json();

      if (!response.ok) {
        if (response.status === 404) {
          return {
            hasAccount: false,
            payoutsEnabled: false,
            detailsSubmitted: false,
          };
        }
        throw new Error(data.error || 'Failed to get account status');
      }

      return {
        hasAccount: true,
        accountId: data.accountId,
        payoutsEnabled: data.payoutsEnabled,
        detailsSubmitted: data.detailsSubmitted,
        requirements: data.requirements,
      };
    } catch (error) {
      console.error('❌ Error getting Connect status:', error);
      return {
        hasAccount: false,
        payoutsEnabled: false,
        detailsSubmitted: false,
      };
    }
  }

  /**
   * Get Express Dashboard login link for user to manage their account
   *  SECURITY: Now sends auth token to server

   */
  async getExpressDashboardLink(userId: string): Promise<{ url?: string; error?: string }> {
    try {
      //  SECURITY: Use authedFetch instead of plain fetch

      const response = await authedFetch(`${FUNCTIONS_URL}/getExpressDashboardLink`, {
        method: 'POST',
        body: JSON.stringify({
          // userId is now taken from auth token on server
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to get dashboard link');
      }

      return { url: data.url };
    } catch (error) {
      console.error('❌ Error getting dashboard link:', error);
      return {
        error: error instanceof Error ? error.message : 'Failed to get dashboard link',
      };
    }
  }

  /**
   * Request a payout (triggers admin review first)
   *  SECURITY: Now sends auth token to server + uses atomic transaction

   */
  async requestPayout(userId: string, amount: number): Promise<PayoutResult> {
    try {
      console.log(`💸 Requesting payout of $${amount} for user ${userId}`);

      //  SECURITY: Use authedFetch instead of plain fetch

      const response = await authedFetch(STANDALONE_URLS.requestPayout, {
        method: 'POST',
        body: JSON.stringify({
          // userId is now taken from auth token on server
          amount,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to request payout');
      }

      return {
        success: true,
        payoutId: data.redemptionId,
        amount: data.amount,
      };
    } catch (error) {
      console.error('❌ Payout request error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to request payout',
      };
    }
  }

  // ============================================================
  // UTILITY METHODS
  // ============================================================

  /**
   * Check if Stripe is properly configured
   */
  async isConfigured(): Promise<boolean> {
    const stripe = await this.getStripe();
    return stripe !== null;
  }

  /**
   * Format amount as currency
   */
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  }
}

export const stripeService = new StripeService();
export default stripeService;